import { NmLoader } from '../NmLoader';
import { ChartGuideButton } from '../charts/ChartGuideButton';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NmButton } from '../NmButton';
import { NmCard } from '../NmCard';
import { NmSegmentControl } from '../NmSegmentControl';
import { NmIsoDateField } from '../NmIsoDateField';
import type { AthleteItem } from '../../services/api';
import { workouts } from '../../services/api';
import { useCoachPeakTrends } from '../../hooks/useCoachPeakTrends';
import { CoachWeeklyPeaksChart } from './CoachWeeklyPeaksChart';
import { CoachPeakPointSheet } from './CoachPeakPointSheet';
import { PeakTrendsSelectField } from './PeakTrendsSelectField';
import { WorkoutAnalysisView } from '../../pages/WorkoutAnalysisView';
import type { WorkoutDetail } from '../../services/api';
import {
  formatPeakTrendValue,
  peakTrendUnit,
  type PeakTrendPoint,
} from '../../utils/coachPeakTrends';
import {
  resolveRecordHistoryParams,
  coachTrendPeakOptionsForCategory,
  defaultCoachTrendPeakType,
  RECORD_RANGE_OPTIONS,
  defaultCustomDateRange,
  type PeakCategory,
  type RecordMetricType,
  type RecordRangeId,
} from '../../utils/recordHistoryEnums';

interface CoachPeakTrendsPanelProps {
  athletes: AthleteItem[];
  refreshKey: number;
  compact?: boolean;
  onRemove?: () => void;
}

export function CoachPeakTrendsPanel({ athletes, refreshKey, compact = false, onRemove }: CoachPeakTrendsPanelProps) {
  const { t, i18n } = useTranslation();
  const [category, setCategory] = useState<PeakCategory>('power');
  const [peakType, setPeakType] = useState(defaultCoachTrendPeakType('power'));
  // Defaults: power · watts · 20 min · 90 days · one athlete for a readable first view.
  const [rangeId, setRangeId] = useState<RecordRangeId>('d90');
  const [customStartDraft, setCustomStartDraft] = useState(() => defaultCustomDateRange().start);
  const [customEndDraft, setCustomEndDraft] = useState(() => defaultCustomDateRange().end);
  const [appliedCustomRange, setAppliedCustomRange] = useState<{ start: string; end: string } | null>(
    null,
  );
  const [sportId, setSportId] = useState('all');
  const [metricType, setMetricType] = useState<RecordMetricType>('value');
  const [workoutTypes, setWorkoutTypes] = useState<{ id: number; name?: string; english_name?: string }[]>([]);
  const [visibleIds, setVisibleIds] = useState<Set<number>>(() => new Set());
  // Keep focused selections above the chart, which unmounts while filters load.
  // null means defaults have not been chosen yet; [] is an intentional empty selection.
  const [selectedAthleteIds, setSelectedAthleteIds] = useState<number[] | null>(null);
  useEffect(() => {
    const roster = new Set(athletes.map(athlete => athlete.id));
    setSelectedAthleteIds(current => {
      if (current === null) return current;
      const kept = current.filter(id => roster.has(id));
      return kept.length === current.length ? current : kept;
    });
  }, [athletes]);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [carouselPoints, setCarouselPoints] = useState<PeakTrendPoint[]>([]);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [detailWorkout, setDetailWorkout] = useState<{ workout: WorkoutDetail; userId: number } | null>(null);

  const peakOptions = coachTrendPeakOptionsForCategory(category);

  useEffect(() => {
    void workouts.getWorkoutTypes().then((res) => {
      setWorkoutTypes(res.data?.data?.workoutTypes ?? []);
    });
  }, []);

  useEffect(() => {
    setPeakType(defaultCoachTrendPeakType(category));
    if (category === 'heart_rate' || category === 'pace') {
      setMetricType('value');
    }
  }, [category]);

  useEffect(() => {
    if (!athletes.length) {
      setVisibleIds(new Set());
      return;
    }
    setVisibleIds(new Set([athletes[0].id]));
  }, [athletes, compact]);

  const filters = useMemo(
    () => ({
      category,
      peakType,
      rangeId,
      sportId,
      metricType,
      customStartDate: appliedCustomRange?.start ?? '',
      customEndDate: appliedCustomRange?.end ?? '',
      customRangeApplied: rangeId === 'custom' && !!appliedCustomRange,
    }),
    [appliedCustomRange, category, metricType, peakType, rangeId, sportId],
  );

  const { series, loading, error, hasAnyPoints } = useCoachPeakTrends({
    athletes,
    filters,
    refreshKey,
  });

  const unit = peakTrendUnit(category, metricType);
  const chartRange = resolveRecordHistoryParams(rangeId, peakType, sportId, metricType, appliedCustomRange ?? undefined);

  const intervalLabel = useCallback(
    (opt: { id: string; fa_name: string }) =>
      i18n.language === 'fa' || i18n.language === 'ar' ? opt.fa_name : opt.id.replace(/^(power|heart_rate|pace)/, '').replace('sec', ' s').replace('min', ' min'),
    [i18n.language],
  );

  const rangeOptions = useMemo(
    () =>
      RECORD_RANGE_OPTIONS.map((opt) => ({
        value: opt.id,
        label: opt.labelKey ? t(opt.labelKey, opt.fallback) : opt.fallback,
        group: opt.groupKey ? t(opt.groupKey) : undefined,
      })),
    [t],
  );

  const intervalOptions = useMemo(
    () =>
      peakOptions.map((o) => ({
        value: o.id,
        label: intervalLabel(o),
      })),
    [peakOptions, intervalLabel],
  );

  const sportOptions = useMemo(
    () => [
      { value: 'all', label: t('records.allSports', 'All sports') },
      ...workoutTypes.map((wt) => ({
        value: String(wt.id),
        label: wt.english_name ?? wt.name ?? `#${wt.id}`,
      })),
    ],
    [t, workoutTypes],
  );

  const toggleAthlete = (id: number) => {
    setVisibleIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        if (next.size > 1) next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const showAllAthletes = () => {
    setVisibleIds(new Set(series.filter((s) => s.points.length > 0).map((s) => s.athleteId)));
  };

  const hideAllAthletes = () => {
    const withData = series.filter((s) => s.points.length > 0);
    if (withData.length > 0) {
      setVisibleIds(new Set([withData[0].athleteId]));
    }
  };

  const visibleCount = series.filter((s) => visibleIds.has(s.athleteId) && s.points.length > 0).length;

  const customDraftInvalid =
    !customStartDraft || !customEndDraft || customStartDraft > customEndDraft;
  const customAwaitingApply = rangeId === 'custom' && !appliedCustomRange;
  const customRangeIncomplete = rangeId === 'custom' && (customDraftInvalid || customAwaitingApply);

  const handleRangeChange = (next: RecordRangeId) => {
    setRangeId(next);
    if (next === 'custom') {
      setAppliedCustomRange(null);
      if (!customStartDraft || !customEndDraft) {
        const d = defaultCustomDateRange();
        setCustomStartDraft(d.start);
        setCustomEndDraft(d.end);
      }
    }
  };

  const handleApplyCustomRange = () => {
    if (customDraftInvalid) return;
    setAppliedCustomRange({ start: customStartDraft, end: customEndDraft });
  };

  const handlePointSelect = useCallback(
    (point: PeakTrendPoint) => {
      setCarouselPoints([point]);
      setCarouselIndex(0);
      setSheetOpen(true);
    },
    [series, visibleIds],
  );

  const handleOpenAnalysis = useCallback((workout: WorkoutDetail, userId: number) => {
    setDetailWorkout({ workout, userId });
  }, []);

  if (!athletes.length) return null;

  return (
    <NmCard
      className={compact ? 'coach-peak-panel coach-peak-panel--compact' : 'coach-peak-panel'}
      style={{ padding: 'var(--sp-4)', display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 'var(--fs-body)', fontFamily: 'var(--font-display)', fontWeight: 700 }}>
            {t('focusedPeaks.title')}
          </h3>
          <p style={{ margin: '4px 0 0', fontSize: 'var(--fs-caption)', color: 'var(--fg-3)' }}>
            {t('coach.peakTrends.subtitle', 'Compare personal-best progress across your roster')}
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <ChartGuideButton type="peak_records" title={t('coach.peakTrends.title')} />
        {onRemove ? (
          <button
            type="button"
            className="performance-widget__remove"
            onClick={onRemove}
            aria-label={t('charts.workspace.removeWidget')}
            title={t('charts.workspace.removeWidget')}
          >
            ×
          </button>
        ) : null}
        </div>
      </div>

      <div className="coach-peak-toolbar">
        {/* Row 1: primary metric — segment toggles (high-frequency) */}
        <div className="coach-peak-toolbar__row coach-peak-toolbar__row--primary">
          <div className="coach-peak-toolbar__field coach-peak-toolbar__field--segment coach-peak-toolbar__field--category">
            <NmSegmentControl
              options={[
                { value: 'power' as const, label: t('records.power', 'Power') },
                { value: 'heart_rate' as const, label: t('records.heartRate', 'Heart rate') },
                { value: 'pace' as const, label: t('records.pace', 'Pace') },
              ]}
              value={category}
              onChange={setCategory}
            />
          </div>
          {category === 'power' && (
            <div className="coach-peak-toolbar__field coach-peak-toolbar__field--segment coach-peak-toolbar__field--metric">
              <NmSegmentControl
                options={[
                  { value: 'value' as const, label: t('coach.peakTrends.metricWatts', 'Watts') },
                  { value: 'wkg' as const, label: t('coach.peakTrends.metricWkg', 'W/kg') },
                ]}
                value={metricType}
                onChange={setMetricType}
              />
            </div>
          )}
        </div>
        {/* Row 2: secondary params — equal compact selects */}
        <div className="coach-peak-toolbar__row coach-peak-toolbar__row--filters">
          <PeakTrendsSelectField
            className="coach-peak-toolbar__field coach-peak-toolbar__field--filter"
            value={peakType}
            options={intervalOptions}
            onChange={setPeakType}
            sheetTitle={t('coach.peakTrends.intervalLabel', 'Interval')}
          />
          <PeakTrendsSelectField
            className="coach-peak-toolbar__field coach-peak-toolbar__field--filter"
            value={rangeId}
            options={rangeOptions}
            onChange={(v) => handleRangeChange(v as RecordRangeId)}
            sheetTitle={t('coach.peakTrends.range', 'Range')}
          />
          <PeakTrendsSelectField
            className="coach-peak-toolbar__field coach-peak-toolbar__field--filter"
            value={sportId}
            options={sportOptions}
            onChange={setSportId}
            sheetTitle={t('records.sport', 'Sport')}
          />
        </div>

        {rangeId === 'custom' && (
          <div className="coach-peak-toolbar__row coach-peak-toolbar__row--custom">
            <label className="coach-peak-toolbar__field coach-peak-toolbar__field--date">
              <span className="nm-input-label">{t('coach.peakTrends.customFrom', 'From')}</span>
              <NmIsoDateField
                className="coach-peak-toolbar__date"
                value={customStartDraft}
                onChange={setCustomStartDraft}
                max={customEndDraft || undefined}
              />
            </label>
            <label className="coach-peak-toolbar__field coach-peak-toolbar__field--date">
              <span className="nm-input-label">{t('coach.peakTrends.customTo', 'To')}</span>
              <NmIsoDateField
                className="coach-peak-toolbar__date"
                value={customEndDraft}
                onChange={setCustomEndDraft}
                min={customStartDraft || undefined}
              />
            </label>
            <div className="coach-peak-toolbar__field coach-peak-toolbar__field--apply">
              <span className="nm-input-label" aria-hidden="true">
                &nbsp;
              </span>
              <NmButton
                type="button"
                variant="primary"
                size="sm"
                disabled={customDraftInvalid}
                onClick={handleApplyCustomRange}
              >
                {t('coach.peakTrends.applyRange', 'Apply')}
              </NmButton>
            </div>
          </div>
        )}
      </div>

      {loading && (
        <div>
          <NmLoader />
        </div>
      )}

      {!loading && error && (
        <p style={{ margin: 0, color: 'var(--fg-3)', fontSize: 'var(--fs-small)' }}>
          {t('coach.peakTrends.loadError', 'Could not load peak trends.')}
        </p>
      )}

      {!loading && !error && customRangeIncomplete && (
        <p style={{ margin: 0, color: 'var(--fg-3)', fontSize: 'var(--fs-small)', textAlign: 'center', padding: '24px 0' }}>
          {t('coach.peakTrends.customRangeHint', 'Select dates and tap Apply.')}
        </p>
      )}

      {!loading && !error && !customRangeIncomplete && !hasAnyPoints && (
        <p style={{ margin: 0, color: 'var(--fg-4)', fontSize: 'var(--fs-small)', textAlign: 'center', padding: '24px 0' }}>
          {t('records.empty', 'No records yet.')}
        </p>
      )}

      {!loading && hasAnyPoints && !customRangeIncomplete && (
        <>
          <CoachWeeklyPeaksChart
            series={series}
            selectedIds={selectedAthleteIds}
            onSelectionChange={setSelectedAthleteIds}
            unit={unit}
            startDate={typeof chartRange.start_date === 'string' ? chartRange.start_date : undefined}
            endDate={typeof chartRange.end_date === 'string' ? chartRange.end_date : undefined}
            lowerIsBetter={category === 'pace'}
            formatY={(y) => metricType === 'wkg' && category === 'power' ? y.toFixed(1) : formatPeakTrendValue(y, category)}
            onPointSelect={handlePointSelect}
          />

        </>
      )}

      <CoachPeakPointSheet
        open={sheetOpen && !detailWorkout}
        points={carouselPoints}
        index={carouselIndex}
        unit={unit}
        onClose={() => setSheetOpen(false)}
        onIndexChange={setCarouselIndex}
        onOpenAnalysis={handleOpenAnalysis}
      />

      {detailWorkout && (
        <WorkoutAnalysisView
          workout={detailWorkout.workout}
          userId={detailWorkout.userId}
          onClose={() => setDetailWorkout(null)}
        />
      )}
    </NmCard>
  );
}
